import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import { Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import { BehaviorSubject, EMPTY } from 'rxjs';
import {
  switchMap,
  debounceTime,
  finalize,
  tap,
  catchError,
  takeUntil,
  distinctUntilChanged,
} from 'rxjs/operators';
import {
  GlobalEntitySearchPayload,
  GlobalSearchCategory,
  SearchCategory,
  searchCategories,
  resultsCountText,
} from '../../models/global-entity-search.model';
import { PhoneCallService } from '../../services/phone-call.service';
import {
  GlobalSearchService,
  dealLink,
  contactLink,
  propertyLink,
  companyLink,
} from '../../services/global-search.service';
import { MessageHandlerService } from '../../services/message-handler.service';
import { CallResponse } from '../../models/call-response.model';
import {MatIconModule} from '@angular/material/icon';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatSelectModule} from '@angular/material/select';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MailToDirective} from '@shared/mail-to-directive/mail-to.directive';
import {
  CompaniesHierarchyFilter,
  CompaniesHierarchyToggleComponent
} from '@shared/components/companies-hierarchy-toggle/companies-hierarchy-toggle.component';
import {BaseComponent} from '@shared/components/base-component';
import {MatTooltip} from '@angular/material/tooltip';
import {User, UserService} from '../../../core/services/user.service'
import { HasHighlightsComponent } from '@shared/has-highlights/has-highlights.component';
import { inject } from '@angular/core';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

const trackId = (_index: number, item: any) => item.trackId;

@Component({
    selector: 'global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    providers: [provideTranslocoScope('sharedNav')],
  imports: [
    MatIconModule,
    FormsModule,
    MatSelectModule,
    NgStyle,
    NgClass,
    MatProgressSpinnerModule,
    AsyncPipe,
    MailToDirective,
    MatDialogModule,
    ReactiveFormsModule,
    CompaniesHierarchyToggleComponent,
    MatTooltip,
    HasHighlightsComponent,
    TranslocoPipe
]
})
export class GlobalSearchComponent extends BaseComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  public GlobalSearchCategory = GlobalSearchCategory;
  public defaultCompare = Object.is;
  public trackId = trackId;

  currentIndex?: number;

  @Input() public userId!: number;

  private historyStorageKey() { return `${this.userId}SearchHistory` }

  public searchHistory: string[] = [];
  public searchHistoryLimit = 10;
  public searchTerms!: string;
  @ViewChild('historyDialogRef') public historyDialogRef!: TemplateRef<any>;
  @ViewChild('globalSearchBox') public myButtonRef!: ElementRef;
  @ViewChild('globalSearchInput') public globalSearchInput!: ElementRef;

  public openedDialogRef!: MatDialogRef<any>;
  public searchCategories: SearchCategory[];
  public selectedSearchCategory = GlobalSearchCategory.Global;
  public payload: GlobalEntitySearchPayload = { search: '', take: 3, type: GlobalSearchCategory.Global };
  private searchSubject = new BehaviorSubject<GlobalEntitySearchPayload>(this.payload);

  public noResults = false;
  public showLoading = false;
  public showCloseIcon = false;
  public dealLink = dealLink;
  public contactLink = contactLink;
  public propertyLink = propertyLink;
  public companyLink = companyLink;
  public makeCallInProgress = false;
  public companiesFilterControl = new FormControl<string>(CompaniesHierarchyFilter.All);

  private readonly GlobalSearchResultCount = 3;
  private readonly CategorySearchResultCount = 10;

  public constructor(
    private router: Router,
    private historyDialog: MatDialog,
    private searchService: GlobalSearchService,
    private changeDetector: ChangeDetectorRef,
    private messageService: MessageHandlerService,
    private callService: PhoneCallService,
    private userService: UserService
  ) {
    super();
    this.searchCategories = searchCategories;

    this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        const route = this.router.url;
        if (route.includes('/search')) {
          const urlTree = this.router.parseUrl(route);
          const keyParam = urlTree.queryParams['key'];
          this.searchTerms = keyParam || '';
          this.changeDetector.detectChanges();
          return;
        }
        this.searchTerms = '';
        this.changeDetector.detectChanges();
      }
    });
    this.userService.user$
      .subscribe((user : User) => {
          if (user.companyParentSearchByDefault) {
            this.companiesFilterControl.setValue(CompaniesHierarchyFilter.Parent);
          }
        });
  }

  public ngOnInit() {
    this.initializeSearchHistory();

    this.companiesFilterControl.valueChanges.pipe(
      takeUntil(this._destroy),
      distinctUntilChanged(),
      debounceTime(800),
    ).subscribe(() => this.showResults());
  }

  public initializeSearchHistory() {
    if (!localStorage.getItem(`${this.userId}SearchHistory`)) {
      localStorage.setItem(`${this.userId}SearchHistory`, JSON.stringify(this.searchHistory));
    } else {
      this.searchHistory = JSON.parse(localStorage.getItem(`${this.userId}SearchHistory`)!);
    }
  }

  public updateSearchHistoryStorage() {
    if (!this.searchTerms || this.searchTerms.length < 3) {
      return;
    }
    const history = JSON.parse(localStorage.getItem(this.historyStorageKey())!) as string[];

    // Don't add this search term if it's already in our history, instead move it to top.
    let limitedHistory = history.filter(
      item => item.toLowerCase() !== this.searchTerms.toLowerCase(),
    );
    // if searched term doesn't exist in history
    if (limitedHistory.length === history.length) {
      limitedHistory = history.slice(0, this.searchHistoryLimit - 1);
    }

    const adjustedHistory = [this.searchTerms, ...limitedHistory];

    this.searchHistory = adjustedHistory;
    localStorage.setItem(this.historyStorageKey(), JSON.stringify(this.searchHistory));
  }

  public deleteSearchHistory(target: string) {
    const storageKey = this.historyStorageKey();
    const historyItems = JSON.parse(localStorage.getItem(storageKey)!) as string[];

    this.searchHistory = historyItems.filter(
      item => item.toLowerCase() !== target.toLowerCase(),
    );

    // Write our updated history back into local storage.
    localStorage.setItem(storageKey, JSON.stringify(this.searchHistory));
  }

  public searchResult = this.searchSubject.pipe(
    debounceTime(500),
    switchMap(
      payload => this.searchService.getGlobalEntitySearchResults(payload)
        .pipe(
          finalize(() => {
            this.showLoading = false;
            this.updateSearchHistoryStorage();
          }),
          catchError(() => {
            this.messageService.showMessage({
              isError: true,
              content: this.transloco.translate('sharedNav.globalSearch.searchError'),
            });

            return EMPTY;
          }),
        ),
    ),
    tap((data) => {
      this.noResults = data.companiesTotal === 0 && data.contactsTotal === 0
        && data.dealsTotal === 0 && data.propertiesTotal === 0;
    }),
  );

  public showResults() {
    setTimeout(() => {
      this.globalSearchInput?.nativeElement?.focus();
    }, 100);
    if (!this.searchTerms) { return }

    this.showLoading = true;
    this.noResults = false;
    this.payload.search = this.searchTerms;
    this.payload.type = this.selectedSearchCategory;
    this.payload.parentsOnly = this.companiesFilterControl.value === CompaniesHierarchyFilter.Parent && this.selectedSearchCategory === GlobalSearchCategory.Company;
    this.payload.take = this.selectedSearchCategory === GlobalSearchCategory.Global ? this.GlobalSearchResultCount : this.CategorySearchResultCount;
    this.searchSubject.next(this.payload);
  }

  public showHistoryDialog() {
    this.initializeSearchHistory();
    this.searchTerms = '';
    this.selectedSearchCategory = GlobalSearchCategory.Global;
    const rect: DOMRect = this.myButtonRef.nativeElement.getBoundingClientRect();

    this.openedDialogRef = this.historyDialog.open(this.historyDialogRef, {
      position: {
        top: `${rect.top}px`,
        left: `${rect.left - 25}px`,
      },
      panelClass: 'global-search-overlay',
    });
    setTimeout(() => {
      this.globalSearchInput?.nativeElement?.focus();
    }, 300);
  }

  public displayResult(results: any[], category: GlobalSearchCategory) {
    return (
      !this.showLoading
      && results?.length > 0
      && (this.selectedSearchCategory === GlobalSearchCategory.Global
        || this.selectedSearchCategory === category)
    );
  }

  // TODO (Amer) message service isn't stable yet
  public makeCall(phoneId?: number) {
    if (!phoneId || this.makeCallInProgress) {
      return;
    }

    this.makeCallInProgress = true;
    this.callService.makeCall(phoneId).subscribe((data: CallResponse) => {
      this.makeCallInProgress = false;
      this.messageService.showMessage({
        content: data.message || this.transloco.translate('sharedNav.globalSearch.phoneCallRequest'),
        isError: !data.success,
      });
      this.changeDetector.detectChanges();
    });
  }

  public navigate(routeLink?: string) {
    if (this.searchTerms && this.searchTerms !== '') {
      this.changeDetector.detectChanges();
      this.openedDialogRef.close();
      this.openedDialogRef.afterClosed().subscribe(() => {
        const adjustedRouteLink = routeLink || 'search';
        let queryParams = {};
          if (!routeLink) {
            queryParams = { key: this.searchTerms, category: this.selectedSearchCategory,_: crypto.randomUUID() };

            if (this.selectedSearchCategory === GlobalSearchCategory.Company) {
              queryParams = {
                ...queryParams,
                companiesFilter: this.companiesFilterControl.value,
              }
            }
          }

        this.router.navigate([adjustedRouteLink], { queryParams });
      });
    }
  }

  public navigateToViewAll(category: GlobalSearchCategory) {
    this.selectedSearchCategory = category;
    this.changeDetector.detectChanges();
    this.openedDialogRef.close();
    this.openedDialogRef.afterClosed().subscribe(() => {
      let queryParams: Record<string, any> = { key: '', category, _: crypto.randomUUID() };
      if (category === GlobalSearchCategory.Company) {
        queryParams['companiesFilter'] = this.companiesFilterControl.value;
      }
      this.router.navigate(['search'], { queryParams });
    });
  }

  public resultsCountText = resultsCountText;
}
